import React, { useCallback, useContext } from 'react';
import Confetti from 'react-confetti';
import ReactDOMServer from 'react-dom/server';
import { useHistory } from 'react-router-dom';
import { PageWizardProps } from '.';
import Button from '../../../components/Button';
import ContactSupportLink from '../../../components/ContactSupportLink';
import CopyToClipboardLink from '../../../components/CopyToClipboardLink';
import Error from '../../../components/Error';
import Header from '../../../components/Header';
import Loader from '../../../components/Loader';
import { Pill } from '../../../components/Pill';
import SnippetBox from '../../../components/SnippetBox';
import { SnippetContainerBox, SnippetFlexWrapper } from '../../../components/SnippetContainer';
import {
    TableHeadResponsive,
    TableHeadThResponsive,
    TableResponsive,
    TableRowResponsive,
    TableRowTdResponsive,
} from '../../../components/TableResponsive';
import Tabs from '../../../components/Tabs';
import { CurrentCompanyContext } from '../../../hooks/CurrentCompanyContext';
import { ROUTES } from '../../../routes/routes';
import { Event } from '../../../types/event';
import { PaymentFlowType, Plan, PricingPage } from '../../../types/pricingPage';
import { SNIPPET_SERVER_URL } from '../../../utils/constants';
import { formatFrequency, formatPrice } from '../../../utils/currency';
import { useAxios, useCopySnippet } from '../../../utils/hooks';
import { isCustomProduct, isStripePrice } from '../../../utils/products';
import Footer from './Footer';
import { withPageIdParam } from './utils';
import HtmlTagName from '../../../components/HtmlTagName';
import ExternalLink from '../../../components/ExternalLink';

const getPageSnippetUrl = (pageId: string) => `${SNIPPET_SERVER_URL}/${pageId}/pricewell.js`;
enum IdentifierType {
    NONE = 'none',
    EMAIL = 'email',
    STRIPE_ID = 'stripeId',
}
export const getPageSnippet = (page: PricingPage, identifier: IdentifierType = IdentifierType.NONE) => {
    const containerProps: any = {};
    if (identifier === IdentifierType.EMAIL) {
        containerProps['data-email'] = 'CUSTOMER_EMAIL_HERE';
    } else if (identifier === IdentifierType.STRIPE_ID) {
        containerProps['data-stripe-id'] = 'CUSTOMER_STRIPE_ID_HERE';
    }

    return ReactDOMServer.renderToStaticMarkup(
        <>
            <div {...containerProps} id={`pricewell-${page.id}`}></div>
            <script src={getPageSnippetUrl(page.id)} async={true}></script>
        </>
    );
};
export const getCheckoutSnippet = (page: PricingPage) =>
    `<script src="${SNIPPET_SERVER_URL}/checkout.js?mode=${
        page.status === 'ACTIVE' ? 'live' : 'test'
    }" async="async"></script>`;
export const getCheckoutFnSnippet = (page: PricingPage) =>
    `<script>window.pricewell.checkout('${page.id}',${
        page.plans.length === 1 ? "'" + page.plans[0].id + "'" : 'null'
    }, {${
        page.plans[0].subscriptionSettings.skipPayment ? 'email:"CUSTOMER_EMAIL_HERE", name: "CUSTOMER_NAME"' : ''
    }});</script>`;
const getCustomButtonSnippet = (page: PricingPage, plan: Plan) =>
    `<button onclick="window.pricewell.checkout('${page.id}',${"'" + plan.id + "'"});">${
        page.plans[0].designSettings.buttonText
    }</button>`;
const getStandaloneWebpageUrl = (page: PricingPage) => `${SNIPPET_SERVER_URL}/pricing/${page.id}`;

export const needsCheckoutSnippet = (plans: Plan[]) =>
    plans.some((plan) => plan.paymentFlow.type === PaymentFlowType.CHARGE_LATER);

export default function CopySnippet({ page }: PageWizardProps) {
    const axios = useAxios();
    const fnSnippet = getCheckoutFnSnippet(page);
    const company = useContext(CurrentCompanyContext);

    const checkEvents = useCallback(() => {
        if (!axios || !axios.current) {
            return Promise.resolve([]);
        }
        return axios.current
            .get<Event[]>(`/pricing-pages/${page.id}/events?limit=1&sinceMinsAgo=10`)
            .then((res) => res.data);
    }, [axios, page.id]);
    const [snippetDetected, handleCopyToClipboard, copied, showHelp] = useCopySnippet(checkEvents, 'pricing_page');

    const showCheckoutSnippet = needsCheckoutSnippet(page.plans);
    const isStripeConnected = !!company?.stripe?.stripeId;

    return (
        <>
            <div className="grid grid-cols-1 gap-2 text-center mb-8">
                <Header>Embed your Pricing Page</Header>
                {!isStripeConnected && (
                    <div className="text-center flex text-sm items-center my-4 bg-white p-4">
                        <Pill colour="YELLOW" title="Stripe not connected" />

                        <div className="ml-4">Some options are disabled until you connect your Stripe Account.</div>
                    </div>
                )}
                <Tabs
                    tabs={[
                        {
                            title: 'Standard',
                            id: 'standard',
                            body: (
                                <SnippetBody
                                    title="Display your Pricing Page"
                                    copied={copied}
                                    handleCopyToClipboard={handleCopyToClipboard}
                                    page={page}
                                    showCheckoutSnippet={showCheckoutSnippet}
                                    showHelp={showHelp}
                                    snippetDetected={snippetDetected}
                                    snippetIdentifier={IdentifierType.NONE}
                                />
                            ),
                        },
                        {
                            title: 'Web Link',
                            id: 'web-link',
                            body: (
                                <SnippetContainerBox>
                                    <h3 className="font-lg font-semibold text-lg underline">
                                        Send a direct link to your pricing
                                    </h3>
                                    <p className="pb-4">
                                        Send the following{' '}
                                        <ExternalLink href={getStandaloneWebpageUrl(page)}>link</ExternalLink> to your
                                        customers who can choose their pricing and complete checkout.
                                    </p>

                                    <SnippetBox snippet={getStandaloneWebpageUrl(page)} />
                                </SnippetContainerBox>
                            ),
                        },
                        {
                            title: 'Custom Button',
                            id: 'custom-button',
                            disabled: !isStripeConnected,
                            body: showCheckoutSnippet ? null : (
                                <SnippetContainerBox>
                                    <h3 className="font-lg font-semibold text-lg underline">
                                        Need a custom checkout button without the pricing page?
                                    </h3>
                                    <p className="py-2">
                                        Copy the following into the <HtmlTagName name="head" /> tag of your HTML.
                                    </p>

                                    <SnippetBox snippet={getCheckoutSnippet(page)} />

                                    <p className="py-2">
                                        Copy the following into the <HtmlTagName name="body" /> tag of your HTML for
                                        each plan.
                                    </p>
                                    {page.plans
                                        .filter(({ product }) => !isCustomProduct(product))
                                        .map((plan) => (
                                            <React.Fragment key={plan.id}>
                                                <h3 className="font-bold py-2">
                                                    {plan.name} -{' '}
                                                    {isStripePrice(plan.product)
                                                        ? formatPrice(plan.product) +
                                                          ' ' +
                                                          formatFrequency(plan.product)
                                                        : ''}
                                                </h3>

                                                <SnippetBox snippet={getCustomButtonSnippet(page, plan)} />
                                            </React.Fragment>
                                        ))}
                                </SnippetContainerBox>
                            ),
                        },
                        {
                            title: 'Existing Users',
                            id: 'existing-users',
                            disabled: !isStripeConnected,
                            body: showCheckoutSnippet ? null : (
                                <SnippetBody
                                    title="Display your Pricing Page to existing Stripe Customers"
                                    copied={copied}
                                    handleCopyToClipboard={handleCopyToClipboard}
                                    page={page}
                                    showCheckoutSnippet={showCheckoutSnippet}
                                    showHelp={showHelp}
                                    snippetDetected={snippetDetected}
                                    description={
                                        <>
                                            <p>
                                                If you are placing your pricing page where you can identify your
                                                customer, then PriceWell will automatically check and link the checkout
                                                to that customer.
                                            </p>
                                            <p className="my-4">Choose one of the following snippets.</p>
                                            <TableResponsive>
                                                <TableHeadResponsive>
                                                    <TableRowResponsive>
                                                        <TableHeadThResponsive>Description</TableHeadThResponsive>
                                                        <TableHeadThResponsive>Attribute</TableHeadThResponsive>
                                                        <TableHeadThResponsive>Copy Snippet</TableHeadThResponsive>
                                                    </TableRowResponsive>
                                                </TableHeadResponsive>
                                                <tbody className="text-gray-600 text-sm font-light">
                                                    <TableRowResponsive>
                                                        <TableRowTdResponsive>
                                                            <span className="font-medium text-base">
                                                                I know the Stripe Customer Email
                                                            </span>
                                                        </TableRowTdResponsive>
                                                        <TableRowTdResponsive>
                                                            <span className="font-medium text-base">data-email</span>
                                                        </TableRowTdResponsive>
                                                        <td className="py-3 px-6 text-left">
                                                            <div className="w-52">
                                                                <SnippetBox
                                                                    snippet={getPageSnippet(page, IdentifierType.EMAIL)}
                                                                    onCopyToClipboard={handleCopyToClipboard}
                                                                />
                                                            </div>
                                                        </td>
                                                    </TableRowResponsive>
                                                    <TableRowResponsive>
                                                        <TableRowTdResponsive>
                                                            <span className="font-medium text-base">
                                                                I know the Stripe Customer ID
                                                            </span>
                                                        </TableRowTdResponsive>
                                                        <TableRowTdResponsive>
                                                            <span className="font-medium text-base">
                                                                data-stripe-id
                                                            </span>
                                                        </TableRowTdResponsive>
                                                        <TableRowTdResponsive>
                                                            <div className="w-52">
                                                                <SnippetBox
                                                                    snippet={getPageSnippet(
                                                                        page,
                                                                        IdentifierType.STRIPE_ID
                                                                    )}
                                                                    onCopyToClipboard={handleCopyToClipboard}
                                                                />
                                                            </div>
                                                        </TableRowTdResponsive>
                                                    </TableRowResponsive>
                                                </tbody>
                                            </TableResponsive>
                                        </>
                                    }
                                />
                            ),
                        },
                        {
                            icon: <img src="/images/integrations/bubble-io.png" className="w-4" alt="" />,
                            title: 'Bubble',
                            id: 'bubble',
                            disabled: !isStripeConnected,
                            body: (
                                <SnippetContainerBox>
                                    <h3 className="font-lg font-semibold text-lg underline mb-4">
                                        Install on Bubble.io
                                    </h3>
                                    <div className="text-left px-4 space-y-4">
                                        <p>
                                            Install the PriceWell{' '}
                                            <a
                                                href="https://bubble.io/plugin/pricewell-1643279429035x716037702183813100"
                                                className="link"
                                            >
                                                Bubble Plugin
                                            </a>
                                        </p>
                                        <img
                                            src="https://help.pricewell.com/img/bubble-plugin.png"
                                            className="w-full mb-4"
                                            alt=""
                                        />
                                        <p className="py-4">
                                            Copy the Page ID <CopyToClipboardLink name="ID" value={page.id} />
                                        </p>
                                        <p>
                                            Drag a Pricing Page element onto your Bubble app. Paste the ID into the
                                            "Pricing Page Id" field.
                                        </p>
                                        <img
                                            src="https://help.pricewell.com/img/bubble-configure-pricing-table.png"
                                            className="w-full"
                                            alt=""
                                        />
                                        <p className="font-bold">
                                            Read the{' '}
                                            <a
                                                href="https://help.pricewell.com/integrations/bubble/"
                                                className="link"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                complete step-by-step documentation
                                            </a>
                                            .
                                        </p>
                                    </div>
                                </SnippetContainerBox>
                            ),
                        },
                    ]}
                />

                {showCheckoutSnippet && (
                    <SnippetFlexWrapper>
                        <Tabs
                            tabs={[
                                {
                                    title: 'Checkout',
                                    id: 'checkout',
                                    body: (
                                        <SnippetContainerBox>
                                            <h3 className="font-lg font-semibold text-lg underline">
                                                2. Go to Checkout
                                            </h3>
                                            <p className="py-2">
                                                First copy the following into the <HtmlTagName name="head" /> tag of
                                                your HTML.
                                            </p>

                                            <SnippetBox snippet={getCheckoutSnippet(page)} />

                                            <p className="py-2 mt-8">
                                                Then copy the following into the <HtmlTagName name="body" /> tag of the
                                                page on which you wish to redirect the customer to checkout.
                                            </p>

                                            <SnippetBox snippet={fnSnippet} />

                                            <hr className="mt-8 py-4" />
                                            <p className="py-2">
                                                <span className="underline">OR</span> choose the use the snippet for the
                                                product you wish to send the customer to.
                                            </p>
                                            {page.plans
                                                .filter(({ product }) => !isCustomProduct(product))
                                                .map((plan) => (
                                                    <>
                                                        <h3 className="font-bold py-2">
                                                            {plan.name} -{' '}
                                                            {isStripePrice(plan.product)
                                                                ? formatPrice(plan.product) +
                                                                  ' ' +
                                                                  formatFrequency(plan.product)
                                                                : ''}
                                                        </h3>

                                                        <SnippetBox
                                                            snippet={fnSnippet.replace('null', `"${plan.id}"`)}
                                                        />
                                                    </>
                                                ))}
                                        </SnippetContainerBox>
                                    ),
                                },
                            ]}
                        />
                    </SnippetFlexWrapper>
                )}
                {snippetDetected && <Success page={page} />}
            </div>
            <Footer
                prev={{ href: ROUTES.PAGES_NEW_DESIGN, disabled: false }}
                next={{ href: ROUTES.PAGES, disabled: false, label: 'Finish' }}
                item={page}
            />
        </>
    );
}

function SnippetBody(props: {
    page: PricingPage;
    showCheckoutSnippet: boolean;
    handleCopyToClipboard: () => void;
    snippetDetected: boolean;
    copied: boolean;
    showHelp: boolean;
    snippetIdentifier?: IdentifierType;
    title: string;
    description?: React.ReactNode;
}) {
    return (
        <SnippetContainerBox>
            <h3 className="font-lg font-semibold text-lg underline">
                {props.showCheckoutSnippet && '1. '}
                {props.title}
            </h3>
            {props.description && <div className="py-2">{props.description}</div>}
            {props.snippetIdentifier && (
                <>
                    <p className="py-2">
                        Copy the following into the <HtmlTagName name="body" /> tag of your HTML at the{' '}
                        <span className="font-bold">exact</span> place you want your pricing page to display.
                    </p>

                    <SnippetBox
                        snippet={getPageSnippet(props.page, props.snippetIdentifier)}
                        onCopyToClipboard={props.handleCopyToClipboard}
                    />
                    {props.snippetDetected && (
                        <div className="flex items-center justify-items-center">
                            <Confetti recycle={false} />
                        </div>
                    )}
                </>
            )}

            {!props.snippetIdentifier && props.snippetDetected && (
                <div className="flex items-center justify-center">
                    <Confetti recycle={false} />
                </div>
            )}

            {props.copied && !props.snippetDetected && (
                <div className="flex place-items-center place-content-center">
                    Checking for snippet...
                    <Loader full={false} />
                </div>
            )}

            {props.showHelp && (
                <div className="pb-6">
                    <Error
                        message={
                            <span>
                                We didn't detect your snippet yet. Need help? <ContactSupportLink />
                            </span>
                        }
                    />
                </div>
            )}
        </SnippetContainerBox>
    );
}

function Success(props: { page: PricingPage }) {
    const history = useHistory();
    const handleClickEdit = () => {
        history.push(withPageIdParam(ROUTES.PAGES_NEW, props.page.id));
    };

    return (
        <>
            <div className="flex flex-col place-items-center mt-4">
                <div className="text-xl">We detected your snippet ✨</div>
                <div className="py-4 text-xl">Do you want to customize it further?</div>
                <div className="flex pb-4">
                    <Button kind="secondary" onClick={handleClickEdit} className="mx-3">
                        Edit Page
                    </Button>
                </div>
            </div>
        </>
    );
}
